// SCR-STAFF-102 — تب رضایت‌ها و ترجیحات (AP-PAT-02): وضعیت جاری هر نوع رضایت با نسخه قالب (US-PAT-050؛ متن نمونه با نشان
// «نمونه» — IsSample)، ثبت پذیرش، رد یا انصراف با کانال و برای کودک با ولی (PHX-PAT-0502)، تاریخچه افزایشی، و ترجیحات ارتباطی
// (US-PAT-051: کانال‌ها، بازه ساعت مجاز و زبان). انصراف از پیام تبلیغاتی از MSG نیز اینجا دیده می‌شود (US-PAT-052).
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ConsentChannel,
  type ConsentDecision,
  type ConsentRecordView,
  type ConsentRequest,
  type ConsentStatusView,
  type ContactChannel,
  type ContactPreferencesRequest,
  type PatientView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, SampleBadge, toJalaliDateTime, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { patKeys, usePatientInvalidation } from './common';

const CHANNELS: ConsentChannel[] = ['InPerson', 'Phone', 'Sms', 'Portal', 'Kiosk'];
const CONTACT_CHANNELS: ContactChannel[] = ['Sms', 'Messenger', 'Email', 'Voice'];

function stateColor(state: ConsentStatusView['state']): 'success' | 'error' | 'default' | 'warning' {
  switch (state) {
    case 'Granted':
      return 'success';
    case 'Denied':
    case 'Withdrawn':
      return 'error';
    case 'RenewalRequired':
      return 'warning';
    default:
      return 'default';
  }
}

function useConsentTemplates() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: patKeys.consentTemplates(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/consent-templates')),
    staleTime: 60_000,
  });
}

function RecordConsentDialog({
  patientId,
  consent,
  requiresGuardian,
  onClose,
}: {
  patientId: string;
  consent: ConsentStatusView | null;
  requiresGuardian: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const invalidate = usePatientInvalidation(patientId);
  const idempotency = useIdempotencyKey();
  const templates = useConsentTemplates();
  const relations = useQuery({
    queryKey: patKeys.relations(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/relations', { params: { path: { id: patientId } } })),
    enabled: consent !== null && requiresGuardian,
  });
  const [decision, setDecision] = useState<ConsentDecision>('Granted');
  const [channel, setChannel] = useState<ConsentChannel>('InPerson');
  const [guardian, setGuardian] = useState('');
  useEffect(() => {
    if (consent) {
      setDecision(consent.state === 'Granted' ? 'Withdrawn' : 'Granted');
      setChannel('InPerson');
      setGuardian('');
    }
  }, [consent]);
  const template = templates.data?.items
    .filter((item) => item.type === consent?.type)
    .sort((a, b) => Number(b.version) - Number(a.version))[0];
  const guardians = (relations.data?.items ?? []).filter((item) => item.type === 'Guardian' && item.isActive && item.direction === 'Outgoing');
  const save = useMutation({
    mutationFn: (body: ConsentRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}/consents', {
          params: { path: { id: patientId } },
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.consents(session?.tenantId, patientId), data);
      invalidate();
      onClose();
    },
  });
  return (
    <FormDialog
      open={consent !== null}
      titleKey="pat:consents.record"
      screenId="SCR-STAFF-102-CONSENT"
      onSubmit={() =>
        consent &&
        save.mutate({
          type: consent.type,
          decision,
          channel,
          templateVersion: template?.version ?? null,
          guardianPatientId: guardian || null,
        })
      }
      onClose={onClose}
      isDirty
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      {consent && (
        <Typography variant="subtitle1">
          {t(`pat:consents.types.${consent.type}`)}
          {template ? ` · ${t('pat:consents.version', { version: faDigits(template.version) })}` : ''}
        </Typography>
      )}
      {template && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, maxHeight: 220, overflow: 'auto' }} data-testid="consent-template-text">
          {template.isSample && <SampleBadge />}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: template.isSample ? 1 : 0 }}>
            {template.body}
          </Typography>
        </Box>
      )}
      <TextField select label={t('pat:consents.decision_label')} value={decision} onChange={(event) => setDecision(event.target.value as ConsentDecision)} fullWidth>
        {(['Granted', 'Denied', 'Withdrawn'] as const).map((value) => (
          <MenuItem key={value} value={value}>
            {t(`pat:consents.decision.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField select label={t('pat:consents.channel_label')} value={channel} onChange={(event) => setChannel(event.target.value as ConsentChannel)} fullWidth>
        {CHANNELS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`pat:consents.channel.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {requiresGuardian && (
        <>
          <Alert severity="info">{t('pat:consents.guardian_required')}</Alert>
          <TextField
            select
            label={t('pat:consents.guardian')}
            value={guardian}
            onChange={(event) => setGuardian(event.target.value)}
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': 'consent-guardian' } }}
          >
            <MenuItem value="">{t('pat:fields.none')}</MenuItem>
            {guardians.map((item) => (
              <MenuItem key={item.relatedPatientId} value={item.relatedPatientId}>
                {item.relatedDisplayName}
              </MenuItem>
            ))}
          </TextField>
        </>
      )}
    </FormDialog>
  );
}

function ContactPreferencesSection({ patientId, canManage }: { patientId: string; canManage: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const preferences = useQuery({
    queryKey: patKeys.preferences(session?.tenantId, patientId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/contact-preferences', { params: { path: { id: patientId } } })),
  });
  const [channels, setChannels] = useState<ContactChannel[]>([]);
  const [from, setFrom] = useState('08:00');
  const [to, setTo] = useState('21:00');
  const [language, setLanguage] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (preferences.data) {
      setChannels(preferences.data.channels);
      setFrom(preferences.data.allowedFrom.slice(0, 5));
      setTo(preferences.data.allowedTo.slice(0, 5));
      setLanguage(preferences.data.language ?? '');
    }
  }, [preferences.data]);
  const save = useMutation({
    mutationFn: (body: ContactPreferencesRequest) =>
      unwrap(tenantApi.PUT('/api/v1/patients/patients/{id}/contact-preferences', { params: { path: { id: patientId } }, body })),
    onMutate: () => setSaved(false),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.preferences(session?.tenantId, patientId), data);
      setSaved(true);
    },
  });
  const rangeInvalid = from >= to;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="contact-preferences">
      <Typography variant="h2" gutterBottom>
        {t('pat:preferences.title')}
      </Typography>
      {preferences.error ? <ErrorState error={preferences.error} compact onRetry={() => void preferences.refetch()} /> : null}
      <Stack spacing={2}>
        {saved && <Alert severity="success">{t('pat:preferences.saved')}</Alert>}
        {preferences.data?.isDefault && <Typography color="text.secondary">{t('pat:preferences.default')}</Typography>}
        <FormGroup row>
          <FormLabel component="legend" sx={{ width: '100%' }}>
            {t('pat:preferences.channels')}
          </FormLabel>
          {CONTACT_CHANNELS.map((channel) => (
            <FormControlLabel
              key={channel}
              control={
                <Checkbox
                  checked={channels.includes(channel)}
                  disabled={!canManage}
                  onChange={(event) =>
                    setChannels(event.target.checked ? [...channels, channel] : channels.filter((item) => item !== channel))
                  }
                />
              }
              label={t(`pat:preferences.channel.${channel}`)}
            />
          ))}
        </FormGroup>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            type="time"
            label={t('pat:preferences.from')}
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            disabled={!canManage}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { dir: 'ltr' } }}
          />
          <TextField
            type="time"
            label={t('pat:preferences.to')}
            value={to}
            onChange={(event) => setTo(event.target.value)}
            disabled={!canManage}
            error={rangeInvalid}
            helperText={rangeInvalid ? t('errors:validation.range') : undefined}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { dir: 'ltr' } }}
          />
          <TextField
            label={t('pat:fields.language')}
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            disabled={!canManage}
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 8 } }}
          />
        </Stack>
        {save.error ? <ErrorState error={save.error} compact /> : null}
        {canManage && (
          <div>
            <Button
              variant="contained"
              disabled={save.isPending || rangeInvalid}
              onClick={() => save.mutate({ channels, allowedFrom: from, allowedTo: to, language: language || null })}
              data-testid="preferences-save"
            >
              {t('common:actions.save')}
            </Button>
          </div>
        )}
      </Stack>
    </Paper>
  );
}

export function PatientConsentsPanel({ patient, canManage }: { patient: PatientView; canManage: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const consents = useQuery({
    queryKey: patKeys.consents(session?.tenantId, patient.id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/consents', { params: { path: { id: patient.id } } })),
  });
  const [recording, setRecording] = useState<ConsentStatusView | null>(null);
  const columns: DataTableColumn<ConsentStatusView>[] = [
    { id: 'type', headerKey: 'pat:consents.type_label', cell: (row) => t(`pat:consents.types.${row.type}`) },
    {
      id: 'state',
      headerKey: 'pat:consents.state_label',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip size="small" color={stateColor(row.state)} label={t(`pat:consents.state.${row.state}`)} data-testid={`consent-state-${row.type}`} />
          {row.isDefault && <Chip size="small" variant="outlined" label={t('pat:consents.default')} />}
        </Stack>
      ),
    },
    {
      id: 'version',
      headerKey: 'pat:consents.version_label',
      cell: (row) => (row.templateVersion ? faDigits(row.templateVersion) : null),
    },
    { id: 'recorded', headerKey: 'pat:consents.recorded_at', cell: (row) => (row.recordedAt ? toJalaliDateTime(row.recordedAt) : null) },
    {
      id: 'actions',
      headerKey: 'pat:consents.record',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => setRecording(row)} data-testid={`consent-record-${row.type}`}>
            {t('pat:consents.record')}
          </Button>
        ) : null,
    },
  ];
  const historyColumns: DataTableColumn<ConsentRecordView>[] = [
    { id: 'at', headerKey: 'pat:consents.recorded_at', cell: (row) => toJalaliDateTime(row.recordedAt) },
    { id: 'type', headerKey: 'pat:consents.type_label', cell: (row) => t(`pat:consents.types.${row.type}`) },
    { id: 'decision', headerKey: 'pat:consents.decision_label', cell: (row) => t(`pat:consents.decision.${row.decision}`) },
    { id: 'channel', headerKey: 'pat:consents.channel_label', cell: (row) => t(`pat:consents.channel.${row.channel}`) },
    { id: 'version', headerKey: 'pat:consents.version_label', cell: (row) => faDigits(row.templateVersion) },
    { id: 'by', headerKey: 'pat:consents.recorded_by', cell: (row) => row.guardianName ?? row.recordedBy ?? row.source },
  ];
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h2" gutterBottom>
          {t('pat:consents.title')}
        </Typography>
        {consents.data?.requiresGuardian && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {t('pat:consents.guardian_required')}
          </Alert>
        )}
        <DataTable
          captionKey="pat:consents.title"
          columns={columns}
          rows={consents.data?.current}
          isLoading={consents.isPending}
          error={consents.error}
          onRetry={() => void consents.refetch()}
          getRowId={(row) => row.type}
        />
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          {t('pat:consents.history')}
        </Typography>
        <DataTable
          captionKey="pat:consents.history"
          columns={historyColumns}
          rows={consents.data?.history}
          isLoading={consents.isPending}
          getRowId={(row) => row.id}
          emptyTitleKey="pat:consents.history_empty"
        />
      </Paper>
      <ContactPreferencesSection patientId={patient.id} canManage={canManage} />
      <RecordConsentDialog
        patientId={patient.id}
        consent={recording}
        requiresGuardian={consents.data?.requiresGuardian ?? false}
        onClose={() => setRecording(null)}
      />
    </Stack>
  );
}
